import { Col, Row } from 'antd';
import React from 'react';

import { useSocketReport } from '@/hooks';
import { PreviousValues } from './PreviousValues';
import { ToolCardGroup } from '@/components/ToolCard';
import { useStepKeysContext } from './DefaultSelects/context';
import { useApplicationContext } from '@/context/v1/Application/context';
import type { ErgonomicTools, REPORT_TYPE, ToolsReponse } from '@/hooks';
import { useEwaJdsD86Context } from '@/components/views/EwaJdsD86/context';
import { AdditionalComponents, DefaultSelects, SubStepKeys } from './index';
import { useHeavyLiftingKimLhcWarning } from './useHeavyLiftingKimLhcWarning';
import { useGetCustomReportResultStepKeyDefaultData } from '@/hooks/useGetCustomReportResultStepKeyDefaultData';

import * as S from './styles';
import { BodyPadding } from '@/components/ui/CollapseCheck/styles';

enum Tools {
	niosh = 'niosh',
	kim_pp = 'kim_pp',
	kim_mho = 'kim_mho',
	kim_lhc = 'kim_lhc',
	strain_index = 'strain_index',
	liberty_mutual = 'liberty_mutual',
	back_compressive_force_estimation = 'back_compressive_force_estimation'
}

const reports: { [key in REPORT_TYPE]: Tools } = {
	niosh: Tools.niosh,
	'kim-pp': Tools.kim_pp,
	'strain-index': Tools.strain_index,
	'kim-mho': Tools.kim_mho,
	'kim-lhc': Tools.kim_lhc,
	'liberty-mutual': Tools.liberty_mutual,
	'back-compressive-force-estimation': Tools.back_compressive_force_estimation
};

export function StepKey(): JSX.Element {
	const { niosh, kimPushPull, kimMHO, strainIndex, libertyMutual, kimLHC } = useSocketReport();
	const { file_id, stepKey, formFieldName } = useStepKeysContext();
	const { ewaJdsD86 } = useEwaJdsD86Context();
	const { organization, company } = useApplicationContext();

	const { data: customReportDefaultData, isFetching: gettingCustomReportDefaultData } =
		useGetCustomReportResultStepKeyDefaultData({
			company_id: company?.id,
			organization_id: organization?.id,
			step_key_id: stepKey.id,
			file_id
		});

	const socketsValues: { [key: string]: boolean | undefined } = {
		niosh: niosh.status,
		'kim-mho': kimMHO.status,
		'kim-pp': kimPushPull.status,
		'kim-lhc': kimLHC.status,
		'strain-index': strainIndex.status,
		'liberty-mutual': libertyMutual.status,
		'back-compressive-force-estimation': false
	};

	const kimLhcWarning = useHeavyLiftingKimLhcWarning(stepKey, formFieldName);

	const ergonomicTools = stepKey.ergonomic_tool ?? [];
	const hasErgonomicTools = ergonomicTools.length > 0;

	function isActive(tool: ErgonomicTools): boolean {
		const reportTool = ewaJdsD86.tools[reports[tool.description] as keyof ToolsReponse];

		return !!reportTool || !!socketsValues[tool.description];
	}

	if (stepKey?.sub_step_keys && stepKey.sub_step_keys.length > 0) {
		return (
			<SubStepKeys
				subStepKeys={stepKey?.sub_step_keys}
				customReportDefaultData={customReportDefaultData}
				gettingCustomReportDefaultData={gettingCustomReportDefaultData}
			/>
		);
	}

	return (
		<>
			<PreviousValues negative={true} />
			<BodyPadding>
				<Row justify="center">
					<Col sm={24} xxl={22}>
						<DefaultSelects
							stepKeyId={stepKey.id}
							formFieldName={formFieldName}
							customReportDefaultData={customReportDefaultData}
							gettingCustomReportDefaultData={gettingCustomReportDefaultData}
						>
							{stepKey.additional_items && (
								<AdditionalComponents
									additionalItems={stepKey.additional_items}
									defaultData={customReportDefaultData?.additional_item_results ?? []}
								/>
							)}
						</DefaultSelects>

						{hasErgonomicTools && (
							<S.ToolCardGroupWrapper>
								<ToolCardGroup
									fixedCardWidth
									fileId={file_id}
									tools={ergonomicTools}
									isActive={isActive}
									showInfoBox={kimLhcWarning.show}
									infoBoxMessage={kimLhcWarning.message}
								/>
							</S.ToolCardGroupWrapper>
						)}
					</Col>
				</Row>
			</BodyPadding>
		</>
	);
}
